<script lang="ts">
	import { browser } from '$app/environment';
	import { page } from '$app/state';
	import CartHeader from '$lib/components/cart/CartHeader.svelte';
	import MarkdownEditor from '$lib/components/ui/MarkdownEditor.svelte';
	import type { Market } from '$lib/types/market';
	import type { LocalsUserType } from '$lib/types/user';
	import { sanitizeHtml } from '$lib/utils/sanitize';
	import '$styles/legacy-application.css';
	import '$styles/legacy-grid.css';
	import { marked } from 'marked';
	import { onMount } from 'svelte';
	import { openSentryFeedback, showFeedbackError } from '$lib/utils/feedback';

	let {
		title,
		loggedIn,
		market,
		user,
		flash = {},
		children,
	} = $props<{
		title: string;
		loggedIn: boolean;
		market: Market;
		user: LocalsUserType;
		flash?: { notice?: string; warning?: string };
		children: () => import('svelte').Snippet;
	}>();

	let showHelpBox = $state(false);
	let showEditSloganBox = $state(false);
	let showEditFooterBox = $state(false);
	let savingSlogan = $state(false);
	let savingFooter = $state(false);
	let saveMessage = $state<string | null>(null);

	// Function to trigger Sentry feedback with improved error handling
	async function openFeedback() {
		const success = await openSentryFeedback(user, loggedIn);
		if (!success) {
			showFeedbackError();
		}
	}

	// Configure marked
	marked.use({
		async: false,
		pedantic: false,
		gfm: true,
		breaks: true,
	});

	// Use derived values to convert markdown to HTML
	const sloganContent = $derived(() => {
		if (!market?.slogan) return '';
		try {
			return marked(market.slogan, { breaks: true, gfm: true }) as string;
		} catch {
			return market.slogan;
		}
	});

	const footerContent = $derived(() => {
		if (!market?.footer) return '';
		try {
			return marked(market.footer, { breaks: true, gfm: true }) as string;
		} catch {
			return market.footer;
		}
	});

	let editableFooterContent = $state(market?.footer || '');
	let editableSloganContent = $state(market?.slogan || '');

	async function updateSlogan() {
		savingSlogan = true;
		saveMessage = null;

		try {
			const response = await fetch('/api/market/update-field', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					field: 'slogan',
					value: editableSloganContent,
				}),
			});

			if (!response.ok) {
				throw new Error('Failed to update slogan');
			}

			showEditSloganBox = false;
			saveMessage = 'Header updated successfully!';
			window.scrollTo({ top: 0, behavior: 'smooth' });

			// Reload after a short delay to show the message
			setTimeout(() => {
				window.location.reload();
			}, 1500);
		} catch (error) {
			console.error('Failed to update slogan:', error);
			saveMessage = 'Error updating header. Please try again.';
			setTimeout(() => {
				saveMessage = null;
			}, 5000);
		} finally {
			savingSlogan = false;
		}
	}

	async function updateFooter() {
		savingFooter = true;
		saveMessage = null;

		try {
			const response = await fetch('/api/market/update-field', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					field: 'footer',
					value: editableFooterContent,
				}),
			});

			if (!response.ok) {
				throw new Error('Failed to update footer');
			}

			showEditFooterBox = false;
			saveMessage = 'Footer updated successfully!';
			window.scrollTo({ top: 0, behavior: 'smooth' });

			// Reload after a short delay to show the message
			setTimeout(() => {
				window.location.reload();
			}, 1500);
		} catch (error) {
			console.error('Failed to update footer:', error);
			saveMessage = 'Error updating footer. Please try again.';
			setTimeout(() => {
				saveMessage = null;
			}, 5000);
		} finally {
			savingFooter = false;
		}
	}

	onMount(() => {
		if (!browser) return;

		// Track if component is still mounted
		let isMounted = true;

		// Initialize Google Analytics
		const _gaq = window._gaq || [];
		_gaq.push(['_setAccount', 'UA-85339-4']);
		_gaq.push(['_trackPageview']);

		if (market?.analytics_code) {
			_gaq.push(['t2._setAccount', market.analytics_code]);
			_gaq.push(['t2._trackPageview']);
		}

		const ga = document.createElement('script');
		ga.type = 'text/javascript';
		ga.async = true;
		ga.src = 'https://www.google-analytics.com/ga.js';
		(
			document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]
		).appendChild(ga);

		// Inject theme and custom CSS in proper order
		// Use async IIFE to handle the await
		(async () => {
			// Wait for all resources to load before injecting custom styles
			await new Promise<void>((resolve) => {
				// Check if already unmounted
				if (!isMounted) return resolve();

				if (document.readyState === 'complete') {
					resolve();
				} else {
					const handleLoad = () => {
						// Check again when load fires
						if (isMounted) resolve();
					};
					window.addEventListener('load', handleLoad, { once: true });
				}
			});

			// Abort if component was unmounted while waiting
			if (!isMounted) return;

			// Find all existing styles to insert after them
			const existingStyles = document.head.querySelectorAll(
				'style:not([data-custom-css]):not([data-theme-url]):not([data-custom-css-url]), link[rel="stylesheet"]:not([data-custom-css-url]):not([data-theme-url])'
			);
			const insertAfter = existingStyles[existingStyles.length - 1] || null;

			// 1. First, inject theme URL if available
			let theme = market?.theme || 'none';
			if (theme === 'ocean' || theme === 'harvest') {
				theme = 'none';
			}
			const theme_url = `/stylesheets/themes/${theme}/${theme}.css`;
			const themeLink = document.createElement('link');
			themeLink.rel = 'stylesheet';
			themeLink.type = 'text/css';
			themeLink.href = theme_url;
			themeLink.setAttribute('data-theme-url', 'true');

			if (insertAfter) {
				insertAfter.insertAdjacentElement('afterend', themeLink);
			} else {
				document.head.appendChild(themeLink);
			}

			// 2. Next, inject custom CSS URL if available
			if (market?.customCssUrl) {
				const customLink = document.createElement('link');
				customLink.rel = 'stylesheet';
				customLink.type = 'text/css';
				customLink.href = market.customCssUrl;
				customLink.setAttribute('data-custom-css-url', 'true');

				// Insert after theme or after existing styles
				const themeLink = document.head.querySelector('[data-theme-url]');
				const afterElement = themeLink || insertAfter;

				if (afterElement) {
					afterElement.insertAdjacentElement('afterend', customLink);
				} else {
					document.head.appendChild(customLink);
				}
			}

			// 3. Finally, inject inline custom CSS if available (highest priority)
			if (market?.customCss) {
				const style = document.createElement('style');
				style.textContent = market.customCss;
				style.setAttribute('data-custom-css', 'true');

				// Insert after custom CSS URL or theme URL or existing styles
				const customCssLink = document.head.querySelector('[data-custom-css-url]');
				const themeLink = document.head.querySelector('[data-theme-url]');
				const afterElement = customCssLink || themeLink || insertAfter;

				if (afterElement) {
					afterElement.insertAdjacentElement('afterend', style);
				} else {
					document.head.appendChild(style);
				}
			}
		})(); // End of async IIFE

		// Cleanup on unmount
		return () => {
			// Mark as unmounted to prevent async operations
			isMounted = false;

			// Remove dynamically injected styles
			document.head
				.querySelectorAll('[data-theme-url], [data-custom-css-url], [data-custom-css]')
				.forEach((el) => {
					el.remove();
				});
		};
	});

	function isCurrentPath(path: string): boolean {
		return page.url.pathname === path;
	}

	function startsWithPath(path: string): boolean {
		return page.url.pathname.startsWith(path);
	}
</script>

<svelte:head>
	<title>{title}</title>
	<!-- Allow pinch-to-zoom and scrolling on mobile for legacy pages -->
	<meta
		name="viewport"
		content="width=device-width, initial-scale=1.0, minimum-scale=0.5, maximum-scale=5.0, user-scalable=yes"
	/>
	<meta
		name="keywords"
		content="locally grown,locally grown food,local food,farmers market,farmers markets, locavore"
	/>
	<meta
		name="description"
		content="{market?.meta_description ||
			''} Locally grown food from local growers! LocallyGrown.net takes the best things about traditional farmers markets, CSAs, and buying clubs and wraps them all together in an online system that's easy for both the grower and the consumer. Find a locally grown market near you, and if there isn't one, start one!"
	/>
	<link rel="icon" href="/favicon.ico" type="image/x-icon" />
	<link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
	<link rel="apple-touch-icon" href="/apple-touch-icon-iphone-60x60.png" />
	<link rel="apple-touch-icon" sizes="60x60" href="/apple-touch-icon-ipad-76x76.png" />
	<link rel="apple-touch-icon" sizes="114x114" href="/apple-touch-icon-iphone-retina-120x120.png" />
	<link rel="apple-touch-icon" sizes="144x144" href="/apple-touch-icon-ipad-retina-152x152.png" />
	<!-- Theme and custom CSS URLs moved to onMount for proper ordering -->
</svelte:head>

<div class="container_12" id="wrapper">
	<div id="pageheader" style="position: relative;">
		<div style="position: relative;">
			<div>
				{#if market}
					{@html sanitizeHtml(sloganContent())}
				{:else}
					<h2><center>LocallyGrown.Net</center></h2>
				{/if}
			</div>
			{#if user?.isAdmin && market}
				<div class="clear"></div>
				<button
					type="button"
					class="edit-button header-edit-button"
					style="background: #4CAF50; color: white; padding: 8px 16px; border: none; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.2); font-size: 14px; cursor: pointer; display: flex; align-items: center; gap: 8px; white-space: nowrap; margin-top: 10px;"
					onclick={() => (showEditSloganBox = !showEditSloganBox)}
					onkeydown={(e) => {
						if (e.key === 'Enter') {
							showEditSloganBox = !showEditSloganBox;
						}
					}}
					aria-label="Edit header text"
					title="Click to edit the header text"
				>
					<img
						loading="lazy"
						src="/images/page_edit.png"
						height="16"
						width="16"
						style="border: none; flex-shrink: 0;"
						alt="Edit icon"
					/>
					<span>Edit Header</span>
				</button>
			{/if}
		</div>
	</div>
	<div class="clear"></div>
	<div id="navBar">
		<ul class="navCircle">
			<li id="welcome">
				<a href="/welcome" class:active={isCurrentPath('/welcome')}>About</a>
			</li>
			{#if market}
				<li id="market">
					<a href="/market" class:active={isCurrentPath('/market')}>The Market</a>
				</li>
			{:else}
				<li>
					<a href="/markets/list" class:active={isCurrentPath('/markets/list')}>Our Markets</a>
				</li>
			{/if}
			{#if !market || market.recipesDisabled === 0}
				<li id="recipes">
					<a href="/recipes" class:active={isCurrentPath('/recipes')}>Recipes</a>
				</li>
			{/if}
			<li id="weblog">
				<a href="/weblog" class:active={isCurrentPath('/weblog')}>Weblog</a>
			</li>
			<li id="faq">
				<a href="/faq" class:active={isCurrentPath('/faq')}>Q's and A's</a>
			</li>
			{#if market}
				<li id="growers">
					<a href="/growers" class:active={startsWithPath('/growers')}>Our Growers</a>
				</li>
			{/if}
			{#if user && loggedIn && market}
				<CartHeader />
			{/if}
			{#if !user && market}
				<li><a href="/signin?from={page.url.pathname}">Sign In</a></li>
			{:else if user && loggedIn}
				<li id="admin">
					<a href="/admin" class:active={isCurrentPath('/admin')}>Your Account</a>
				</li>
				<li>
					<a
						href="/user/signout"
						aria-label="Sign out"
						onclick={(e) => {
							e.preventDefault();
							if (confirm('Are you sure you want to sign out?')) {
								window.location.href = '/user/signout';
							}
						}}>Sign Out</a
					>
				</li>
			{/if}
			<li>
				<span class="help">
					<button
						type="button"
						class="icon-button"
						onclick={() => (showHelpBox = !showHelpBox)}
						aria-label="Display help for this page"
					>
						<img
							loading="lazy"
							src="/images/help.png"
							height="16"
							width="16"
							style="border: none;"
							alt="Help"
						/>
					</button>
				</span>
			</li>
			<!-- Feedback button -->
			<li>
				<span class="help">
					<button
						type="button"
						class="icon-button feedback-button"
						onclick={openFeedback}
						aria-label="Send feedback"
						title="Send feedback to LocallyGrown.net"
					>
						<svg
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							style="border: none;"
						>
							<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
						</svg>
					</button>
				</span>
			</li>
		</ul>
	</div>
	<div class="clear"></div>
	<div id="page">
		{#if saveMessage}
			<div class="grid_8 push_2 save-message" class:success={!saveMessage.includes('Error')}>
				{saveMessage}
			</div>
			<div class="clear"></div>
		{/if}
		{#if flash.notice}
			<div class="grid_8 push_2" id="notice">{flash.notice}</div>
			<div class="clear"></div>
		{/if}
		{#if flash.warning}
			<div class="grid_8 push_2" id="warning">{flash.warning}</div>
			<div class="clear"></div>
		{/if}
		<div class="legacy-content" id="content">
			{@render children()}
		</div>
	</div>
	<div class="clear"></div>
	<div id="pagefooter">
		<div class="footer-content">
			{@html sanitizeHtml(footerContent())}
		</div>
		<center>
			{#if user?.isAdmin && market?.id !== 0}
				<button
					type="button"
					class="edit-button footer-edit-button"
					style="background: #4CAF50; color: white; padding: 8px 16px; border: none; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.2); margin: 10px; display: inline-flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; white-space: nowrap;"
					title="Click to edit the footer text"
					onclick={() => (showEditFooterBox = !showEditFooterBox)}
					aria-label="Edit footer text"
				>
					<span>Edit Footer</span>
				</button>
			{/if}
		</center>
		<center>
			This market is using the <a href="http://www.locallygrown.net" target="_blank"
				>Locally Grown</a
			>
			online farmers market system. More information about how your market or farm can also use this
			system can be found at
			<a href="http://locallygrown.net/faq/" target="_blank">locallygrown.net/faq</a>. Legal
			information about using this website can be found at
			<a href="http://locallygrown.net/legal/index" target="_blank">locallygrown.net/legal</a>.
		</center>
	</div>
	<div class="clear"></div>
</div>

{#if showEditSloganBox && user?.isAdmin}
	<div id="edit_slogan_box">
		<div class="edit-box">
			<form
				onsubmit={(e) => {
					e.preventDefault();
					updateSlogan();
				}}
			>
				<h3>Edit Header Text:</h3>
				<MarkdownEditor bind:value={editableSloganContent} rows={5} />
				<div style="margin-top: 10px;">
					<button type="submit" class="save-button" disabled={savingSlogan}>
						{savingSlogan ? 'Saving...' : 'Save Changes'}
					</button>
					<button
						type="button"
						onclick={() => (showEditSloganBox = false)}
						class="cancel-button"
						disabled={savingSlogan}
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	</div>
{/if}

{#if showEditFooterBox && user?.isAdmin}
	<div id="edit_footer_box">
		<div class="edit-box">
			<form
				onsubmit={(e) => {
					e.preventDefault();
					updateFooter();
				}}
			>
				<h3>Edit Footer Text:</h3>
				<MarkdownEditor bind:value={editableFooterContent} rows={10} />
				<div style="margin-top: 10px;">
					<button type="submit" class="save-button" disabled={savingFooter}>
						{savingFooter ? 'Saving...' : 'Save Changes'}
					</button>
					<button
						type="button"
						onclick={() => (showEditFooterBox = false)}
						class="cancel-button"
						disabled={savingFooter}
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	</div>
{/if}

{#if user?.isAdmin && user?.tender_key && market?.id && market?.id !== 0}
	<script type="text/javascript" charset="utf-8">
		Tender = {
			sso: '{user?.tender_key}',
		};
	</script>
	<script src="https://locallygrown.tenderapp.com/tender_widget.js" type="text/javascript"></script>
{/if}

<style>
	#edit_slogan_box,
	#edit_footer_box {
		position: fixed;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		background: white;
		padding: 20px;
		border: 1px solid #ccc;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
		z-index: 1000;
	}

	.icon-button {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
	}

	.icon-button:focus {
		outline: 2px solid #007bff;
		outline-offset: 2px;
	}

	.edit-button:hover {
		background: #45a049 !important;
		transition: background 0.2s ease;
	}

	.header-edit-button:hover {
		transform: translateY(-50%) scale(1.05);
	}

	.header-edit-button:active {
		transform: translateY(-50%) scale(0.95);
	}

	.footer-edit-button:hover {
		transform: scale(1.05);
	}

	.footer-edit-button:active {
		transform: scale(0.95);
	}

	.edit-box {
		margin: 1em 0;
		padding: 1em;
		border: 1px solid #ddd;
		border-radius: 4px;
	}

	.save-button {
		background-color: #4caf50;
		color: white;
		padding: 8px 16px;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		margin-right: 10px;
		font-size: 14px;
	}

	.save-button:hover:not(:disabled) {
		background-color: #45a049;
	}

	.cancel-button {
		background-color: #f44336;
		color: white;
		padding: 8px 16px;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
	}

	.cancel-button:hover:not(:disabled) {
		background-color: #da190b;
	}

	.save-button:disabled,
	.cancel-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.save-message {
		padding: 12px 16px;
		margin-bottom: 20px;
		border-radius: 4px;
		font-weight: 500;
		background-color: #fee;
		color: #dc2626;
		border: 1px solid #fecaca;
		text-align: center;
	}

	.save-message.success {
		background-color: #f0fdf4;
		color: #166534;
		border-color: #bbf7d0;
	}

	@keyframes pulse {
		0%,
		100% {
			opacity: 0.9;
		}
		50% {
			opacity: 1;
		}
	}

	@keyframes bounce {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(-2px);
		}
	}
</style>
